import React, {useEffect, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getSubBrandKeyFromUrl} from '@vio/core/src/_web/brand/utils'
import loadScript from '@vio/core/src/_web/utils/network/loadScript'
import {logger} from '@vio/core/src/utils/logging/logger'

import {CUSTOM_HEADER_HEIGHT} from './constants'

const HpHeader = styled.div`
  --custom-header-height: ${CUSTOM_HEADER_HEIGHT}px;
  height: ${CUSTOM_HEADER_HEIGHT}px;
  background-color: ${cssTheme.colors.background.neutral.primary};

  &#hp-header#hp-header.--relative {
    & > :first-child > header {
      position: relative;
    }
  }

  &#hp-header#hp-header.--fixed {
    &.--floating {
      height: 0;
    }

    & > :first-of-type > header {
      position: fixed;
    }
  }

  &#hp-header#hp-header.--floating > :first-of-type {
    padding: 0;
  }
`

interface Props {
  /** Whether to float the header above the content. Default is `false` */
  isFloating?: boolean
  /** Whether to fix the header to the top of the page. Default is `true` */
  isFixed?: boolean
}

type SubBrandConfig = {
  headerScriptDomain?: string
  market?: string
}

/**
 * Displays a custom header for HolidayPirates, including the loader script
 * @returns A React component that displays a custom header for HolidayPirates
 * @remarks
 * - Loads the HolidayPirates loader script as a side effect
 * - Only works in domains that have been whitelisted by HolidayPirates
 */
export const HolidayPiratesHeader = ({
  isFloating = false,
  isFixed = true
}: Props) => {
  const [headerScriptDomain, setHeaderScriptDomain] = useState<
    string | undefined
  >()
  const [market, setMarket] = useState<string | undefined>()

  const {brand} = useBrand()
  const subBrand = getSubBrandKeyFromUrl(brand)

  useEffect(() => {
    if (subBrand) {
      const subBrandConfig: SubBrandConfig =
        brand.subBrand?.brands[subBrand] || {}

      setHeaderScriptDomain(subBrandConfig.headerScriptDomain)
      setMarket(subBrandConfig.market)
    } else {
      setHeaderScriptDomain('https://www.urlaubspiraten.de')
      setMarket('de')
    }
  }, [brand.subBrand?.brands, subBrand])

  useEffect(() => {
    if (!headerScriptDomain) return
    loadScript(`${headerScriptDomain}/hp-app/web/loader.js`, false).catch(
      error => {
        logger.error('Failed to load HolidayPirates loader script:', error)
      }
    )
  }, [headerScriptDomain])

  return (
    <HpHeader
      id="hp-header"
      data-partner="vio"
      data-market={market}
      className={cx(
        (isFloating || !isFixed) && '--floating',
        isFixed ? '--fixed' : '--relative'
      )}
    />
  )
}
